import React, { useRef, useState, useEffect } from "react";
import { CloudArrowUpIcon, DocumentTextIcon, TrashIcon } from "@heroicons/react/24/outline";

const ResolutionDetailsTab = ({
  register,
  watch,
  errors,
  resolutionFiles,
  setResolutionFiles,
  complaintData,
  setFilesToRemove
}) => {
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (complaintData?.feedback?.file_url && complaintData.feedback.file_url.length > 0) {
      console.log("Loading existing resolution files:", complaintData.feedback.file_url);
      const existingFiles = complaintData.feedback.file_url.map((url, index) => ({
        id: `existing-${Date.now()}-${index}`,
        url: url,
        name: url.split('/').pop() || `Document ${index + 1}`,
        size: 0,
        type: 'application/octet-stream',
        progress: 100,
        status: 'completed',
        isExisting: true,
        markedForRemoval: false
      }));
      
      setResolutionFiles(existingFiles);
    }
  }, [complaintData, setResolutionFiles]);

  const handleFileSelect = (file) => {
    const newFile = {
      file,
      id: Math.random().toString(36).substr(2, 9),
      preview: file.type.startsWith("image/")
        ? URL.createObjectURL(file)
        : null,
      name: file.name,
      size: file.size,
      type: file.type,
      progress: 0,
      status: 'uploading',
      isExisting: false
    };
    
    setResolutionFiles((prev) => [...prev, newFile].slice(0, 5)); 
    simulateFileUpload(newFile.id);
  };

  const simulateFileUpload = (fileId) => {
    let progress = 0;
    const interval = setInterval(() => {
      progress += Math.floor(Math.random() * 15) + 5;
      
      setResolutionFiles((prev) =>
        prev.map((f) =>
          f.id === fileId
            ? { ...f, progress: Math.min(progress, 100) }
            : f
        )
      );

      if (progress >= 100) {
        clearInterval(interval);
        setResolutionFiles((prev) =>
          prev.map((f) =>
            f.id === fileId
              ? { ...f, status: 'completed', progress: 100 }
              : f
          )
        );
      }
    }, 300);
  };

  const handleFileRemove = (fileId) => {
    setResolutionFiles((prev) => {
      const fileToRemove = prev.find(f => f.id === fileId);
      if (fileToRemove?.preview) {
        URL.revokeObjectURL(fileToRemove.preview);
      }
      
      if (fileToRemove?.isExisting && fileToRemove?.url) {
        if (setFilesToRemove) {
          setFilesToRemove(prevRemove => [...prevRemove, fileToRemove.url]);
        }
      }
      
      return prev.filter(f => f.id !== fileId);
    });
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const droppedFiles = Array.from(e.dataTransfer.files);
    droppedFiles.forEach(file => handleFileSelect(file));
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  const handleFileInputChange = (e) => {
    const selectedFiles = Array.from(e.target.files);
    selectedFiles.forEach(file => handleFileSelect(file));
    e.target.value = '';
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const commentField = register("feedback.comment");

  return (
    <div className="space-y-6 animate-in slide-in-from-left duration-300">
      {/* Rating */}
      <div>
        <h3 className="text-md text-gray-700 mb-2">Rating</h3>
        <input
          type="number"
          min="1"
          max="5"
          {...register("feedback.rating", {
            min: { value: 1, message: "Rating must be at least 1" },
            max: { value: 5, message: "Rating must be at most 5" }
          })}
          placeholder="Enter rating from 1 to 5"
          className={`mt-1 block w-full border-2 rounded-lg px-4 py-3 transition-all duration-200 ${
            errors.feedback?.rating
              ? "border-red-300 bg-red-50 focus:border-red-500 focus:ring-red-500"
              : "border-gray-200 hover:border-gray-300 focus:border-blue-500 focus:ring-blue-500"
          } placeholder-gray-400 focus:outline-none focus:ring-1`}
        />
        {errors.feedback?.rating && (
          <p className="text-red-500 text-xs mt-1">
            {errors.feedback.rating.message}
          </p>
        )}
      </div>

      {/* Citizen Feedback Comment */}
      <div>
        <h3 className="text-md text-gray-700 mb-2">Citizen Feedback</h3>
        <textarea
          {...commentField}
          rows={5}
          placeholder="Enter feedback comment here"
          className={`mt-1 block w-full border-2 rounded-lg px-4 py-3 transition-all duration-200 min-h-[150px] ${
            errors.feedback?.comment
              ? "border-red-300 bg-red-50 focus:border-red-500 focus:ring-red-500"
              : "border-gray-200 hover:border-gray-300 focus:border-blue-500 focus:ring-blue-500"
          } placeholder-gray-400 focus:outline-none focus:ring-1`}
        />
        {errors.feedback?.comment && (
          <p className="text-red-500 text-xs mt-1">
            {errors.feedback.comment.message}
          </p>
        )}
      </div>

      {/* Feedback Documents */}
      <div className="w-full">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Resolution Proof
        </label>

        <div className="space-y-3">
          {/* File List */}
          {resolutionFiles.map((file) => (
            <div key={file.id} className="border rounded-lg bg-white p-3 shadow-sm">
              <div className="flex items-center justify-between w-full">
                <div className="flex items-center gap-3 flex-1 min-w-0">
                  <DocumentTextIcon className="w-5 h-5 text-blue-500 flex-shrink-0" />
                  <div className="min-w-0">
                    {file.url ? (
                      <a
                        href={file.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-sm font-medium text-blue-600 truncate hover:underline block"
                      >
                        {file.name}
                      </a>
                    ) : file.preview ? (
                      <a
                        href={file.preview}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-sm font-medium text-blue-600 truncate hover:underline block"
                      >
                        {file.name}
                      </a>
                    ) : (
                      <span className="text-sm font-medium text-gray-700 truncate block">
                        {file.name}
                      </span>
                    )}
                    <div className="flex items-center text-xs text-gray-500 mt-1">
                      <span>
                        {file.progress === 100
                          ? formatFileSize(file.size)
                          : `${formatFileSize(Math.round((file.size * file.progress) / 100))} / ${formatFileSize(file.size)}`}
                      </span>
                      <span className="mx-2">•</span>
                      <span
                        className={`font-medium ${
                          file.progress === 100 ? "text-green-600" : "text-blue-600"
                        }`}
                      >
                        {file.progress === 100 ? (file.isExisting ? "Existing" : "Completed") : "Uploading..."}
                      </span>
                    </div>
                  </div>
                </div>
                <button
                  onClick={() => handleFileRemove(file.id)}
                  className="ml-4 p-2 text-gray-400 hover:text-red-500 rounded-full"
                  type="button"
                >
                  <TrashIcon className="w-5 h-5" />
                </button>
              </div>

              {/* Progress Bar */}
              {file.progress < 100 && !file.isExisting && (
                <div className="w-full bg-gray-200 rounded-full h-1 mt-2 overflow-hidden">
                  <div
                    className="bg-blue-600 h-1 rounded-full transition-all duration-300"
                    style={{ width: `${file.progress}%` }}
                  />
                </div>
              )}
            </div>
          ))}

          {/* Upload Area */}
          {resolutionFiles.length < 5 && (
            <div
              className="w-full border-2 border-dashed border-gray-200 bg-gray-50 rounded-lg cursor-pointer hover:border-blue-300 transition duration-150 text-center"
              onClick={() => fileInputRef.current?.click()}
              onDrop={handleDrop}
              onDragOver={handleDragOver}
            >
              <div className="p-10 text-center">
                <CloudArrowUpIcon className="w-10 h-10 text-gray-400 mx-auto mb-3" />
                <p className="text-sm text-gray-700">
                  Drag and Drop <span className="font-semibold">Documents</span>
                </p>
                <p className="text-xs text-gray-500 mt-1">
                  or{" "}
                  <span className="text-blue-600 font-medium hover:underline">
                    browse files
                  </span>{" "}
                  on your computer
                </p>
                <p className="text-xs text-gray-400 mt-2">
                  Supports: Images, PDF, DOC, DOCX, TXT (Max 5 files)
                </p>
              </div>
              <input
                type="file"
                ref={fileInputRef}
                className="hidden"
                multiple
                accept="image/*,.pdf,.doc,.docx,.txt"
                onChange={handleFileInputChange}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ResolutionDetailsTab;